import dayjs from 'dayjs';
import { useMemo } from 'react';
import useTimeNow from '../../../../hooks/use-time-now';
import {
    clockTimeZones,
    SettingsWorldClockDateFormat,
    SettingsWorldClockItem,
    SettingsWorldClocks,
    SettingsWorldClockTimeFormat
} from '../../../../types/clocks';
import { useAppSelector } from '../../../hooks/store-hooks';
import { useDateTimeFormatter } from '../../../hooks/use-date-time-formatted';

export function TimezoneBar() {
    const clocksSettings = useAppSelector((x) => x.settings.worldClocks);
    const height = clocksSettings.dateFormat === SettingsWorldClockDateFormat.none ? 52 : 67;

    return (
        <>
            {clocksSettings.enabled && (
                <div
                    className="border-bottom"
                    style={{
                        height,
                        backgroundColor: clocksSettings.barColor,
                        display: 'grid',
                        gridAutoFlow: 'column',
                        gridAutoColumns: '1fr'
                    }}>
                    {clocksSettings.clocks.map((clock, i) => (
                        <ClockView key={i} clock={clock} settings={clocksSettings} />
                    ))}
                </div>
            )}
        </>
    );
}

function ClockView(props: { clock: SettingsWorldClockItem; settings: SettingsWorldClocks }) {
    const formatter = useDateTimeFormatter();
    const timeNow = useTimeNow({ intervalSeconds: 60 });
    const date = dayjs.unix(timeNow).tz(props.clock.timeZoneName, false);

    const name = useMemo(() => {
        const timeZone = clockTimeZones.find((x) => x.value === props.clock.timeZoneName);
        if (!timeZone) {
            return null;
        }

        const text = props.clock.useCustomName ? props.clock.customName : timeZone.value;

        if (!props.settings.addOffsetToName) {
            return text;
        }

        const offsetMinutes = date.utcOffset();
        const offsetString = offsetToString(offsetMinutes);
        return `${text ?? ''} ${offsetString}`;
    }, [date, props.clock]);

    const timeFormat = timeFormatStrings[props.settings.timeFormat];
    const dateString =
        props.settings.dateFormat === SettingsWorldClockDateFormat.none
            ? ''
            : props.settings.dateFormat === SettingsWorldClockDateFormat.followGlobal
              ? formatter.formatDate(timeNow)
              : props.settings.dateFormat === SettingsWorldClockDateFormat.dayOfWeekMonthDay
                ? date.format('ddd MMM DD')
                : '';

    const timeString = date.format(timeFormat);
    const startsWithOne = timeString.charAt(0) === '1';

    return (
        <div className="d-flex flex-column align-items-align-items-stretch justify-content-center overflow-hidden">
            <div
                className="font-clock text-center"
                style={{
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                    color: props.settings.timeColor,
                    fontSize: '1.7rem',
                    paddingRight: startsWithOne ? '0.5rem' : undefined
                }}>
                {date.format(timeFormat)}
            </div>
            {dateString && (
                <div
                    className="text-center"
                    style={{
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                        textOverflow: 'ellipsis',
                        fontSize: '0.8rem',
                        color: props.settings.dateColor,
                        textTransform: props.settings.timeZoneUppercase ? 'uppercase' : 'none'
                    }}>
                    {dateString}
                </div>
            )}
            <div
                className="text-center"
                style={{
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                    fontSize: '0.8rem',
                    color: props.settings.nameColor,
                    textTransform: props.settings.timeZoneUppercase ? 'uppercase' : 'none'
                }}>
                {name}
            </div>
        </div>
    );
}

const timeFormatStrings: Record<SettingsWorldClockTimeFormat, string> = {
    [SettingsWorldClockTimeFormat.h24]: 'HH:mm',
    [SettingsWorldClockTimeFormat.h12]: 'hh:mm A'
};

function offsetToString(offsetMinutes: number): string {
    const offsetHours = offsetMinutes / 60;
    const hours = (offsetMinutes >= 0 ? '+' : '-') + Math.trunc(Math.abs(offsetHours)).toString();
    const minutes = Math.round((Math.abs(offsetHours) % 1) * 60);
    return `UTC${hours}${minutes ? ':' + minutes : ''}`;
}
